import { useTranslation } from 'react-i18next';
import { useLivePlans } from '../lib/plans';
import { useAppUrl } from '../lib/appUrl';
import { planFeatureLines } from '../lib/planFeatures';

const PLANS = ['trial', 'starter', 'pro', 'business'];

function ctaPath(plan) {
  if (plan === 'trial') return '/?register=1';
  return `/billing?plan=${plan}&register=1`;
}

// The first two lines of every card are always "N agents" and "N messages".
// With live data they, the price, and every line after them come from the API
// — the rest from the plan features edited in Admin → Pricing. Without it (API
// down, or no feature catalogue yet) the card keeps its typed-in copy.
function cardContent(t, lang, plan, live) {
  const features = t(`pricing.${plan}.features`, { returnObjects: true });
  const staticPrice = t(`pricing.${plan}.price`);
  if (!live) return { price: staticPrice, features };
  const n = (v) => v.toLocaleString('en-US');
  const messagesKey = plan === 'trial' ? 'pricing.live.trialMessages' : 'pricing.live.messages';
  const lines = planFeatureLines(live.catalogue, live.features, lang, {
    unlimited: t('pricing.live.unlimited'),
  });
  const replaced = [
    t('pricing.live.agents', { count: live.maxAgents }),
    t(messagesKey, { n: n(live.maxMessages) }),
    ...(lines ?? features.slice(2)),
  ];
  return {
    price: plan === 'trial' ? staticPrice : t('pricing.live.price', { price: live.price }),
    features: replaced,
  };
}

export default function PricingPreview() {
  const { t, i18n } = useTranslation();
  const livePlans = useLivePlans();
  const appUrl = useAppUrl();
  return (
    <section className="border-b border-border bg-bg">
      <div className="mx-auto max-w-6xl px-4 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold sm:text-4xl">{t('pricing.title')}</h2>
          <p className="mt-4 text-text-muted">{t('pricing.subtitle')}</p>
        </div>
        <div className="mt-14 grid items-stretch gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => {
            const isPopular = plan === 'pro';
            const { price, features } = cardContent(
              t,
              i18n.language,
              plan,
              livePlans?.[plan],
            );
            return (
              <div
                key={plan}
                className={`relative flex flex-col rounded-2xl border p-6 transition duration-200 ${
                  isPopular
                    ? 'border-primary bg-surface glow-primary lg:-translate-y-2'
                    : 'border-border bg-surface hover:-translate-y-1 hover:border-border-strong'
                }`}
              >
                {isPopular && (
                  <span className="absolute -top-3 start-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-fg shadow-lg shadow-primary/30">
                    {t('pricing.pro.popular')}
                  </span>
                )}
                <h3 className="text-lg font-semibold">{t(`pricing.${plan}.name`)}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{price}</span>
                  <span className="text-sm text-text-muted">{t(`pricing.${plan}.period`)}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-2.5 text-sm text-text-muted">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckIcon />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={appUrl(ctaPath(plan))}
                  className={`mt-6 rounded-lg py-2.5 text-center text-sm font-semibold transition ${
                    isPopular
                      ? 'bg-primary text-primary-fg hover:bg-primary-hover'
                      : 'border border-border text-text hover:border-border-strong'
                  }`}
                >
                  {t('pricing.cta')}
                </a>
              </div>
            );
          })}
        </div>
        <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-text-faint">
          {t('pricing.upgradeNote')}
        </p>
      </div>
    </section>
  );
}

function CheckIcon() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
